/**
 * User Clients Modal Component
 * Part of TASK-334: Frontend Admin Panel
 *
 * Modal dialog for managing client assignments to a user.
 * Supports Chinese Wall and assigned-only visibility modes.
 */

import React, { useState, useEffect } from 'react';
import { X, UserPlus, Trash2, User, Building2, AlertTriangle } from 'lucide-react';
import type { UserListItem, ClientAssignment, ClientAssignmentType } from '../../../types/admin';
import { ASSIGNMENT_TYPE_LABELS } from '../../../types/admin';
import { userManagementService } from '../../../services/admin';
import { httpClient } from '../../../lib/httpClient';
import { Button } from '../../ui/button';
import { toast } from 'sonner';
import { extractErrorMessage } from '../../../utils/apiErrorHandler';

interface AdminClient {
  id: string;
  name: string;
  type?: string;
  is_active?: boolean;
}

interface UserClientsModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: UserListItem | null;
  onClientsChanged?: () => void;
}

const UserClientsModal: React.FC<UserClientsModalProps> = ({
  isOpen,
  onClose,
  user,
  onClientsChanged,
}) => {
  const [assignedClients, setAssignedClients] = useState<ClientAssignment[]>([]);
  const [availableClients, setAvailableClients] = useState<AdminClient[]>([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [assignmentType, setAssignmentType] = useState<ClientAssignmentType>('primary');
  const [isLoading, setIsLoading] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  // Load assigned clients and available clients
  useEffect(() => {
    if (isOpen && user) {
      setIsLoading(true);
      Promise.all([
        userManagementService.getUserClients(user.id),
        // Use admin endpoint that bypasses visibility rules
        httpClient.get<{ clients: AdminClient[]; total: number }>('/api/admin/clients'),
      ])
        .then(([userClients, adminClientsResponse]) => {
          setAssignedClients(userClients);
          // Filter out clients already assigned to this user
          const assignedIds = new Set(userClients.map((c) => c.client_id));
          setAvailableClients(
            adminClientsResponse.clients.filter((c) => !assignedIds.has(c.id))
          );
        })
        .catch((err) => {
          console.error('Failed to load data:', err);
          toast.error('Failed to load client assignments');
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [isOpen, user]);

  // Reset form when opening/closing
  useEffect(() => {
    if (!isOpen) {
      setSelectedClientId('');
      setAssignmentType('primary');
    }
  }, [isOpen]);

  const handleAddClient = async () => {
    if (!user || !selectedClientId) return;

    setIsAdding(true);
    try {
      await userManagementService.assignClientToUser(user.id, selectedClientId, assignmentType);

      // Find the client from available clients to add to assigned list
      const addedClient = availableClients.find((c) => c.id === selectedClientId);
      if (addedClient) {
        const newAssignment: ClientAssignment = {
          client_id: addedClient.id,
          client_name: addedClient.name,
          client_type: addedClient.type,
          assignment_type: assignmentType,
          assigned_at: new Date().toISOString(),
        };
        setAssignedClients([...assignedClients, newAssignment]);
        setAvailableClients(availableClients.filter((c) => c.id !== selectedClientId));
      }

      setSelectedClientId('');
      setAssignmentType('primary');
      toast.success('Client assigned successfully');
      onClientsChanged?.();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to assign client'));
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemoveClient = async (clientId: string) => {
    if (!user) return;

    setRemovingId(clientId);
    try {
      await userManagementService.unassignClientFromUser(user.id, clientId);

      // Move client back to available list
      const removedAssignment = assignedClients.find((c) => c.client_id === clientId);
      setAssignedClients(assignedClients.filter((c) => c.client_id !== clientId));

      if (removedAssignment) {
        // Add back to available clients
        setAvailableClients([
          ...availableClients,
          {
            id: removedAssignment.client_id,
            name: removedAssignment.client_name || 'Unknown',
            type: removedAssignment.client_type,
          },
        ]);
      }

      toast.success('Client unassigned successfully');
      onClientsChanged?.();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to unassign client'));
    } finally {
      setRemovingId(null);
    }
  };

  const getClientIcon = (type?: string) => {
    if (type === 'corporate' || type === 'company') {
      return <Building2 size={16} className="text-gray-400" />;
    }
    return <User size={16} className="text-gray-400" />;
  };

  const getAssignmentTypeColor = (type: ClientAssignmentType) => {
    switch (type) {
      case 'primary':
        return 'bg-blue-100 text-blue-800';
      case 'secondary':
        return 'bg-purple-100 text-purple-800';
      case 'viewer':
        return 'bg-gray-100 text-gray-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  if (!isOpen || !user) return null;

  const userName = user.display_name || user.first_name || user.email;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg transform transition-all">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Manage Client Assignments</h2>
              <p className="text-sm text-gray-500">Assign clients to {userName}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4">
            {/* Add client form */}
            <div className="space-y-3 mb-4">
              <div className="flex gap-2">
                <select
                  value={selectedClientId}
                  onChange={(e) => setSelectedClientId(e.target.value)}
                  disabled={isLoading || availableClients.length === 0}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F7B500] disabled:bg-gray-50"
                >
                  <option value="">
                    {isLoading
                      ? 'Loading...'
                      : availableClients.length === 0
                      ? 'No clients available'
                      : 'Select a client...'}
                  </option>
                  {availableClients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name} {client.type ? `(${client.type})` : ''}
                    </option>
                  ))}
                </select>
                <select
                  value={assignmentType}
                  onChange={(e) => setAssignmentType(e.target.value as ClientAssignmentType)}
                  disabled={isLoading}
                  className="w-28 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F7B500] disabled:bg-gray-50"
                >
                  <option value="primary">Primary</option>
                  <option value="secondary">Secondary</option>
                  <option value="viewer">Viewer</option>
                </select>
                <Button
                  onClick={handleAddClient}
                  disabled={!selectedClientId || isAdding}
                  size="sm"
                >
                  <UserPlus size={16} className="mr-1" />
                  Add
                </Button>
              </div>
            </div>

            {/* Info banner */}
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
              <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800">
                Changes take effect immediately. Client visibility depends on your account's visibility mode settings.
              </p>
            </div>

            {/* Assigned clients list */}
            <div className="mb-2">
              <h3 className="text-sm font-medium text-gray-700">
                Assigned Clients ({assignedClients.length})
              </h3>
            </div>

            {isLoading ? (
              <div className="py-8 text-center">
                <div className="animate-spin w-8 h-8 border-2 border-gray-300 border-t-[#002B45] rounded-full mx-auto mb-2" />
                <p className="text-sm text-gray-500">Loading assignments...</p>
              </div>
            ) : assignedClients.length === 0 ? (
              <div className="py-8 text-center text-gray-500 bg-gray-50 rounded-lg">
                <User size={32} className="mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No clients assigned yet</p>
                <p className="text-xs text-gray-400 mt-1">
                  Select a client above to assign it to this user
                </p>
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {assignedClients.map((assignment) => (
                  <div
                    key={assignment.client_id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center">
                        {getClientIcon(assignment.client_type)}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900 text-sm">
                          {assignment.client_name || 'Unknown Client'}
                        </div>
                        <div className="text-xs text-gray-500 capitalize">
                          {assignment.client_type || 'Individual'}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <span
                        className={`px-2 py-0.5 text-xs font-medium rounded ${getAssignmentTypeColor(
                          assignment.assignment_type
                        )}`}
                      >
                        {ASSIGNMENT_TYPE_LABELS[assignment.assignment_type] || assignment.assignment_type}
                      </span>
                      <button
                        onClick={() => handleRemoveClient(assignment.client_id)}
                        disabled={removingId === assignment.client_id}
                        className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                        title="Remove assignment"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex justify-end">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserClientsModal;
